import {
  applyActiveVariants,
  cloneBlockTree,
  fillPhaseContainer,
  findNodeById,
  normalizeBlockForPaint,
} from "../preview/block.js";
import {
  catalogNameForChromePart,
  frameEntryForRef,
  getGlobalElements,
  getPages,
  getSurfaceCard,
  getViewFromCard,
  getWidgets,
  listChromeFrame,
  listChromeFrameRefs,
  listPageKeys,
  normalizeTheme,
  resolvePageView,
} from "../preview/document.js";
import {
  isLocalizedTextRef,
  resolveLocalizedText,
  resolveTitleFields,
} from "../preview/localization.js";

function cloneNode(value) {
  return JSON.parse(JSON.stringify(value));
}

export function resolveElementTree(node, _elements, _errors = []) {
  return cloneBlockTree(node);
}

export function listDatasetRecords(doc, kind) {
  if (!kind) return [];
  const table = getDataset(doc)?.[kind];
  if (!Array.isArray(table)) return [];
  return table.flatMap((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const id = typeof entry.id === "string" ? entry.id : "";
    if (!id) return [];
    const title = typeof entry.title === "string" && entry.title ? entry.title : id;
    return [{ id, title }];
  });
}

/** Drawn tree: widget or page `view`. */
export function widgetVisualTemplate(spec) {
  return getViewFromCard(spec);
}

const WIDGET_CONTRACT_KEYS = new Set(["interface", "actions", "view", "phases"]);
const ELEMENT_META_KEYS = new Set(["skeleton"]);

function stripElementMeta(node) {
  if (!node || typeof node !== "object") return null;
  const visual = {};
  for (const [key, value] of Object.entries(node)) {
    if (ELEMENT_META_KEYS.has(key)) continue;
    visual[key] = cloneNode(value);
  }
  return visual;
}

function isWidgetTemplateRoot(node) {
  if (!node || typeof node !== "object") return false;
  return Boolean(node.type || node.element || node.id);
}

const DATA_PHASE_IDS = {
  skeleton: "phase-skeleton",
  empty: "phase-empty",
  error: "phase-error",
};
const PREVIEW_MODE_TO_PHASE_ID = {
  skeleton: DATA_PHASE_IDS.skeleton,
  empty: DATA_PHASE_IDS.empty,
  error: DATA_PHASE_IDS.error,
};

function widgetPhaseEntries(spec) {
  return Array.isArray(spec?.phases) ? spec.phases : [];
}

function phaseById(spec, id) {
  return widgetPhaseEntries(spec).find((entry) => entry && entry.id === id) ?? null;
}

function resolveDeclaredPhaseId(spec, id) {
  if (typeof id !== "string" || !id) return null;
  return phaseById(spec, id) ? id : null;
}

/** Dev preview default when host phase is omitted (matches public header before login). */
export function defaultPreviewPhaseId(spec) {
  const ids = new Set(widgetPhaseEntries(spec).map((entry) => entry?.id).filter(Boolean));
  if (ids.has("phase-unauthorized")) return "phase-unauthorized";
  return null;
}

/** Default widget preview mode in the viewer (not the host `data` / base view). */
export function defaultWidgetPreviewMode(spec) {
  const modes = widgetListPreviewModes(spec);
  if (modes.includes("phase-unauthorized")) return "phase-unauthorized";
  return modes[0] ?? "data";
}

function walkNodeTree(root, visit) {
  if (!root || typeof root !== "object") return;
  const stack = [cloneBlockTree(root)];
  while (stack.length) {
    const node = stack.pop();
    if (!node || typeof node !== "object") continue;
    visit(node);
    for (const child of node.items || []) stack.push(child);
  }
}

function walkVisualNodes(spec, visit) {
  const root = widgetVisualTemplate(spec);
  if (!root) return;
  walkNodeTree(root, visit);
}

/** Base view plus every `phases[].items` tree (e.g. phase_add_curency lists). */
function walkWidgetSpecNodes(spec, visit) {
  walkVisualNodes(spec, visit);
  for (const phase of widgetPhaseEntries(spec)) {
    for (const item of phase.items ?? []) walkNodeTree(item, visit);
  }
}

function collectDatasetKinds(root, kinds, seen) {
  walkNodeTree(root, (node) => {
    const dataset = node.interaction?.dataset ?? node.dataset;
    if (typeof dataset !== "string" || !dataset || dataset === "children" || seen.has(dataset)) return;
    seen.add(dataset);
    kinds.push(dataset);
  });
}

/**
 * Dataset ids for the preview bar.
 * `data` walks the base view. A phase mode walks that phase's trees so
 * phase_add_curency shows wallet.add_currencies, not the base wallet.accounts list.
 */
export function listWidgetDatasetKinds(spec, previewMode) {
  const kinds = [];
  const seen = new Set();
  const phaseId = previewMode && previewMode !== "data" ? phaseTokenToId(previewMode) : null;
  if (phaseId) {
    for (const phase of widgetPhaseEntries(spec)) {
      if (phase?.id !== phaseId) continue;
      for (const item of phase.items ?? []) collectDatasetKinds(item, kinds, seen);
    }
    if (kinds.length) return kinds;
  }
  const root = widgetVisualTemplate(spec);
  if (root) collectDatasetKinds(root, kinds, seen);
  return kinds;
}

function findFirstDatasetContainer(spec) {
  let found = null;
  walkWidgetSpecNodes(spec, (node) => {
    const dataset = node.interaction?.dataset ?? node.dataset;
    if (!found && node.id && dataset) found = { ...node, dataset };
  });
  return found;
}

function containerFillConfig(row) {
  if (!row?.id || !row.dataset) return null;
  return {
    containerId: row.id,
    datasetKind: row.dataset,
  };
}

function normalizeHostPhaseId(phase) {
  return typeof phase === "string" ? phase : null;
}

function previewModeToPhaseId(previewMode) {
  if (!previewMode || previewMode === "data") return null;
  return PREVIEW_MODE_TO_PHASE_ID[previewMode] ?? null;
}

export function phaseTokenToId(token) {
  if (!token || token === "data") return null;
  return PREVIEW_MODE_TO_PHASE_ID[token] ?? normalizeHostPhaseId(token) ?? token;
}

function resolveActivePhaseId(data, ctx, spec, widgetName) {
  const fromHost = resolveDeclaredPhaseId(spec, normalizeHostPhaseId(data?.phase));
  if (fromHost) return fromHost;
  const stored = widgetName ? ctx.phaseByWidget?.[widgetName] : null;
  if (stored) {
    const storedId = resolveDeclaredPhaseId(spec, phaseTokenToId(stored));
    if (storedId) return storedId;
    return null;
  }
  if (ctx.previewMode === "data") return null;
  const fromPreview = resolveDeclaredPhaseId(spec, phaseTokenToId(ctx.previewMode));
  if (fromPreview) return fromPreview;
  if (ctx.previewMode != null && ctx.previewMode !== "") return null;
  const fallback = defaultPreviewPhaseId(spec);
  if (fallback && phaseById(spec, fallback)) return fallback;
  return null;
}

/** Widget declares `phases` or a dataset container in `states`. */
export function widgetHasListPhasePreview(spec) {
  if (widgetPhaseEntries(spec).length) return true;
  return Boolean(findFirstDatasetContainer(spec));
}

export function widgetListPreviewModes(spec) {
  const phases = widgetPhaseEntries(spec);
  if (!phases.length) {
    return findFirstDatasetContainer(spec) ? ["data"] : [];
  }
  const modes = ["data"];
  const seen = new Set(modes);
  for (const entry of phases) {
    const id = entry?.id;
    if (typeof id !== "string" || !id || seen.has(id)) continue;
    modes.push(id);
    seen.add(id);
  }
  return modes;
}

function interfacePhaseEntries(spec) {
  const raw = spec?.interface;
  if (!raw || typeof raw !== "object") return [];
  const phases = raw.phases;
  return Array.isArray(phases) ? phases : [];
}

/** Title from `widgets.*.interface.phases` (falls back to phase id). */
export function interfacePhaseTitle(spec, phaseId) {
  if (typeof phaseId !== "string" || !phaseId) return null;
  for (const entry of interfacePhaseEntries(spec)) {
    if (!entry || typeof entry !== "object") continue;
    const id = typeof entry.id === "string"
      ? entry.id
      : (typeof entry.name === "string" ? entry.name : null);
    if (id !== phaseId) continue;
    if (typeof entry.title === "string") return entry.title;
    return id;
  }
  return null;
}

export function listInterfacePhases(spec) {
  return interfacePhaseEntries(spec).flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const id = typeof entry.id === "string"
      ? entry.id
      : (typeof entry.name === "string" ? entry.name : null);
    if (!id) return [];
    const title = typeof entry.title === "string" ? entry.title : id;
    return [{ id, title }];
  });
}

/** Preview bar label is the phase id. Base view mode stays `data`. No titles or aliases. */
export function widgetPreviewModeLabel(_spec, mode) {
  return typeof mode === "string" ? mode : "";
}

/** One button per preview mode. Label is that same id. */
export function listWidgetPreviewBarEntries(_spec, listModes) {
  if (!Array.isArray(listModes) || !listModes.length) return [];
  return listModes.map((mode) => ({ mode, label: mode }));
}

/** Dev fixture blob. The published document omits `dataset`. */
export function getDataset(doc) {
  if (doc?.dataset != null && typeof doc.dataset === "object") return doc.dataset;
  return {};
}

/** Widget catalog entry for instance (`template` overrides visual spec). */
export function resolveWidgetCatalogName(node) {
  if (!node?.widget) return null;
  if (typeof node.template === "string" && node.template.trim()) {
    return node.template.trim();
  }
  return node.widget;
}

/** Dataset id referenced by the first list container in the widget view. */
export function widgetPreviewDatasetKind(widgetName, doc) {
  const spec = getWidgets(doc)?.[widgetName];
  const row = findFirstDatasetContainer(spec);
  return typeof row?.dataset === "string" ? row.dataset : null;
}

/** Widget instance for preview: the catalog tree, plus data.phase when a declared phase is selected. */
export function previewWidgetTarget(widgetName, doc, options = {}) {
  const spec = getWidgets(doc)?.[widgetName];
  if (!spec || typeof spec !== "object") return null;
  const phaseId = resolveActivePhaseId(
    {},
    { previewMode: options.previewMode, phaseByWidget: options.phaseByWidget },
    spec,
    widgetName,
  );
  const instance = { widget: widgetName };
  if (phaseId) instance.data = { phase: phaseId };
  const frameEntry = frameEntryForRef(doc, widgetName);
  if (frameEntry?.presentation && typeof frameEntry.presentation === "object") {
    instance.presentation = cloneNode(frameEntry.presentation);
  }
  return instance;
}

/** Catalog element as a drawable contract node. Same path for every element. */
export function previewWidgetElementRender(templateNode) {
  if (!templateNode || typeof templateNode !== "object") return null;
  return stripElementMeta(cloneNode(templateNode));
}

function phasesById(spec, id) {
  return widgetPhaseEntries(spec).filter((entry) => entry && entry.id === id);
}

function expandWidgetPhases(node, ctx, widgetName, spec) {
  const activePhaseId = resolveActivePhaseId(node.data ?? {}, ctx, spec, widgetName);
  if (!activePhaseId) return node;
  for (const phaseEntry of phasesById(spec, activePhaseId)) {
    if (!phaseEntry.container) continue;
    const row = findNodeById(node, phaseEntry.container);
    if (!row) continue;
    fillPhaseContainer(row, phaseEntry);
  }
  return node;
}

export function expandWidgetInstance(merged, spec, ctx, widgetName) {
  if (!widgetPhaseEntries(spec).length) return merged;
  return expandWidgetPhases(merged, ctx, widgetName, spec);
}

function substituteDatasetFields(value, record) {
  if (typeof value === "string") {
    const fromRecord = (name) => {
      if (!record || !Object.prototype.hasOwnProperty.call(record, name)) return null;
      // `{{title}}` uses EN title + optional title_key (never treat title as a dict key).
      if (name === "title") {
        return resolveTitleFields(record);
      }
      const found = record[name];
      if (found == null) return null;
      if (isLocalizedTextRef(found)) {
        return resolveLocalizedText(found);
      }
      if (typeof found === "object") return null;
      return String(found);
    };
    return value
      .replace(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g, (token, name) => fromRecord(name) ?? "")
      .replace(/#\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (token, name) => fromRecord(name) ?? token);
  }
  if (isLocalizedTextRef(value)) {
    return resolveLocalizedText(value);
  }
  if (Array.isArray(value)) return value.map((item) => substituteDatasetFields(item, record));
  if (!value || typeof value !== "object") return value;
  if (typeof value.interaction?.dataset === "string") return value;
  const next = {};
  for (const [key, child] of Object.entries(value)) next[key] = substituteDatasetFields(child, record);
  return next;
}

function treeHasDatasetPlaceholder(value) {
  if (typeof value === "string") return value.includes("{{");
  if (Array.isArray(value)) return value.some((item) => treeHasDatasetPlaceholder(item));
  if (!value || typeof value !== "object") return false;
  return Object.values(value).some((child) => treeHasDatasetPlaceholder(child));
}

function datasetRecords(dataset, kind) {
  const table = dataset?.[kind];
  if (Array.isArray(table)) {
    return table.filter((entry) => entry && typeof entry === "object");
  }
  if (!table || typeof table !== "object") return [];
  return Object.values(table).filter((entry) => entry && typeof entry === "object" && !Array.isArray(entry));
}

function firstDatasetRecord(dataset, kind) {
  return datasetRecords(dataset, kind)[0] ?? null;
}

function datasetKindOf(node) {
  if (!node || typeof node !== "object") return null;
  if (typeof node.dataset === "string") return node.dataset;
  const interaction = node.interaction;
  if (interaction && typeof interaction === "object" && typeof interaction.dataset === "string") {
    return interaction.dataset;
  }
  return null;
}

function findDatasetRecord(node, dataset) {
  if (!node || typeof node !== "object" || !dataset) return null;
  const kind = datasetKindOf(node);
  if (kind) {
    const record = firstDatasetRecord(dataset, kind);
    if (record) return record;
  }
  for (const child of node.items || []) {
    const found = findDatasetRecord(child, dataset);
    if (found) return found;
  }
  return null;
}

function findDatasetTemplateInWidget(spec, kind) {
  if (!spec || typeof spec !== "object") return null;
  let hit = findDatasetTemplateIn(spec.view, kind);
  if (hit) return hit;
  for (const phase of widgetPhaseEntries(spec)) {
    for (const item of phase.items ?? []) {
      hit = findDatasetTemplateIn(item, kind);
      if (hit) return hit;
    }
  }
  return null;
}

function findDatasetTemplateNode(doc, kind) {
  if (!kind) return null;
  const widgets = getWidgets(doc) || {};
  for (const spec of Object.values(widgets)) {
    const hit = findDatasetTemplateInWidget(spec, kind);
    if (hit) return hit;
  }
  return null;
}

function findDatasetTemplateIn(node, kind) {
  if (!node || typeof node !== "object") return null;
  if (datasetKindOf(node) === kind && Array.isArray(node.items) && node.items[0]) return node.items[0];
  for (const child of node.items || []) {
    const hit = findDatasetTemplateIn(child, kind);
    if (hit) return hit;
  }
  return null;
}

/** Named states on the dataset row template. Disabled variants are omitted. */
export function listDatasetContainerStates(doc, kind) {
  const template = findDatasetTemplateNode(doc, kind);
  if (!template) return [];
  const states = [];
  const seen = new Set();
  const stack = [template];
  while (stack.length) {
    const node = stack.pop();
    const variants = node?.variants;
    if (variants && typeof variants === "object") {
      for (const [name, patch] of Object.entries(variants)) {
        if (!name || seen.has(name)) continue;
        if (patch && typeof patch === "object" && patch.enabled === false) continue;
        seen.add(name);
        const description = patch && typeof patch.description === "string" ? patch.description : "";
        states.push({ name, description });
      }
    }
    const children = Array.isArray(node?.items) ? node.items : [];
    for (let index = children.length - 1; index >= 0; index -= 1) stack.push(children[index]);
  }
  return states;
}

function applyNamedVariant(node, name) {
  if (!node || typeof node !== "object" || !name) return;
  const patch = node.variants?.[name];
  if (patch && typeof patch === "object" && patch.enabled !== false) {
    if (patch.appearance && typeof patch.appearance === "object") {
      node.appearance = { ...(node.appearance || {}), ...patch.appearance };
    }
    if (patch.layout && typeof patch.layout === "object") {
      node.layout = { ...(node.layout || {}), ...patch.layout };
    }
  }
  for (const child of node.items || []) applyNamedVariant(child, name);
}

function placeholderNames(value, found = new Set()) {
  if (typeof value === "string") {
    for (const match of value.matchAll(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g)) found.add(match[1]);
    return found;
  }
  if (Array.isArray(value)) {
    for (const item of value) placeholderNames(item, found);
    return found;
  }
  if (value && typeof value === "object") {
    for (const child of Object.values(value)) placeholderNames(child, found);
  }
  return found;
}

function rowRecords(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((row) => row && typeof row === "object" && !Array.isArray(row));
}

function recordsForSlotData(template, data, kind) {
  const named = kind ? rowRecords(data?.[kind]) : [];
  if (named.length) return named;
  const names = [...placeholderNames(template)];
  if (!names.length || !data || typeof data !== "object") return null;
  let best = null;
  for (const value of Object.values(data)) {
    if (!Array.isArray(value)) continue;
    const rows = rowRecords(value);
    if (!rows.length) continue;
    const score = names.filter((name) => rows.some((row) => row[name] != null && row[name] !== "")).length;
    if (!score) continue;
    if (!best || score > best.score) best = { rows, score };
  }
  return best?.rows ?? null;
}

function resolveActiveId(ctx, records) {
  const ids = new Set(records.map((row) => row?.id).filter((id) => typeof id === "string" && id));
  if (typeof ctx.activeLinkId === "string" && ids.has(ctx.activeLinkId)) return ctx.activeLinkId;
  if (ctx.activeLinkId) return "";
  return ctx.data ? activeRecordId(ctx.data, records) : "";
}

function activeRecordId(data, records) {
  const ids = new Set(records.map((row) => row?.id).filter((id) => typeof id === "string" && id));
  if (data && typeof data.active_link_id === "string" && ids.has(data.active_link_id)) {
    return data.active_link_id;
  }
  for (const value of Object.values(data || {})) {
    if (typeof value === "string" && ids.has(value)) return value;
  }
  return "";
}

const NAV_TREE_DATASETS = new Set(["sidebar.navigation", "sidebar.navigation.tail", "children"]);

function recordHasChildren(record) {
  return Array.isArray(record?.children) && record.children.length > 0;
}

function isToggleDisclosureNode(node) {
  if (!node || typeof node !== "object") return false;
  if (node.type === "toggle_disclosure") return true;
  const action = node.interaction?.action;
  return action === "toggle_disclosure";
}

function isEmptyAfterSubstitution(node) {
  if (!node || typeof node !== "object") return false;
  if (node.type === "icon") {
    const url = node.appearance?.source?.url;
    return typeof url !== "string" || !url.trim() || url.includes("{{");
  }
  if (node.type === "text") {
    const text = node.appearance?.text;
    return typeof text !== "string" || !text.trim() || text.includes("{{");
  }
  return false;
}

function pruneNavRowTree(node, record) {
  if (!node || typeof node !== "object") return node;
  const hasChildren = recordHasChildren(record);
  if (Array.isArray(node.items)) {
    node.items = node.items
      .filter((child) => {
        if (!hasChildren) {
          if (isToggleDisclosureNode(child)) return false;
          if (child?.interaction?.dataset === "children") return false;
        }
        return !isEmptyAfterSubstitution(child);
      })
      .map((child) => pruneNavRowTree(child, record));
  }
  return node;
}

function recordsForListKind(kind, template, ctx) {
  if (kind === "children") {
    return rowRecords(ctx.parentRecord?.children);
  }
  if (ctx.data) {
    return recordsForSlotData(template, ctx.data, kind);
  }
  if (ctx.dataset) {
    const expandThis = ctx.activeKind == null || ctx.activeKind === kind;
    if (!expandThis) return null;
    return datasetRecords(ctx.dataset, kind);
  }
  return null;
}

function expandListTree(node, ctx) {
  if (!node || typeof node !== "object") return node;
  const kind = datasetKindOf(node);
  if (kind && Array.isArray(node.items) && node.items.length === 1) {
    const template = node.items[0];
    const records = recordsForListKind(kind, template, ctx);
    const games = kind !== "children" && Array.isArray(records?.[0]?.games) ? records[0].games : null;
    if (games && treeHasDatasetPlaceholder(template)) {
      node.items = games
        .filter((game) => game && typeof game === "object")
        .map((game) => {
          const row = substituteDatasetFields(cloneBlockTree(template), { ...records[0], ...game });
          return expandListTree(row, ctx);
        });
      return node;
    }
    if (records?.length && treeHasDatasetPlaceholder(template)) {
      const activeId = resolveActiveId(ctx, records);
      node.items = records.map((record, index) => {
        let row = substituteDatasetFields(cloneBlockTree(template), record);
        const selected = Boolean(activeId) && record.id === activeId;
        if (selected) {
          applyNamedVariant(row, "selected");
          row.selectedState = true;
        }
        const state = !ctx.data && index === 0 && typeof record.state === "string" ? record.state : "";
        if (state) applyNamedVariant(row, state);
        row = expandListTree(row, { ...ctx, parentRecord: record });
        return NAV_TREE_DATASETS.has(kind) ? pruneNavRowTree(row, record) : row;
      });
      return node;
    }
  }
  for (const child of node.items || []) expandListTree(child, ctx);
  return node;
}

/** Fill list templates from the chrome slot `data`, not the document dataset. */
function expandSlotDataPreview(node, data) {
  if (!node || typeof node !== "object" || !data) return node;
  const activeLinkId = typeof data.active_link_id === "string" ? data.active_link_id : "";
  return expandListTree(node, { data, parentRecord: null, dataset: null, activeKind: null, activeLinkId });
}

/** Repeat `items[0]` for each dataset row and fill `{{field}}` from the preview record. */
function expandDatasetPreview(node, dataset, activeKind, activeLinkId = "") {
  if (!node || typeof node !== "object" || !dataset) return node;
  return expandListTree(node, {
    data: null,
    parentRecord: null,
    dataset,
    activeKind,
    activeLinkId,
  });
}

export function materializeWidgetNode(node, ctx) {
  if (!node || typeof node !== "object") return null;
  const widgetName = resolveWidgetCatalogName(node) ?? node.widget;
  if (!widgetName) return cloneBlockTree(node);
  const spec = ctx.widgets?.[widgetName];
  if (!spec) return null;
  const view = widgetVisualTemplate(spec);
  if (!view) return null;
  let merged = cloneBlockTree(view);
  merged = expandWidgetInstance(merged, spec, { ...ctx, widgetName }, widgetName);
  const activeLinkId = typeof ctx.activeLinkId === "string"
    ? ctx.activeLinkId
    : (typeof ctx.slotData?.active_link_id === "string" ? ctx.slotData.active_link_id : "");
  if (ctx.slotData && typeof ctx.slotData === "object") {
    return expandSlotDataPreview(merged, ctx.slotData);
  }
  merged = expandDatasetPreview(merged, ctx.dataset, ctx.previewDatasetKind, activeLinkId);
  if (ctx.previewDatasetKind === "") return merged;
  const record = findDatasetRecord(merged, ctx.dataset);
  if (record) return substituteDatasetFields(merged, record);
  return merged;
}
